import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../constants/api';
import EditMemberModal from '../components/EditMemberModal';
import DeleteMemberModal from '../components/DeleteMemberModal';

type Member = {
  id: number;
  name: string;
  email: string;
  phoneNumber: string;
  business_Or_Employer: string;
  applied_On: string;
  status: string;
};

interface MembersPageProps {
  members: Member[];
  searchQuery: string;
  onSearchChange: (value: string) => void;
  onEdit?: (index: number) => void;
  onDelete?: (index: number) => void;
  defaultFilter?: 'All' | 'Pending' | 'Approved' | 'Rejected';
}

const MembersPage: React.FC<MembersPageProps> = ({ members, searchQuery, onSearchChange, onEdit, onDelete, defaultFilter = 'All' }) => {
  const [statusFilter, setStatusFilter] = useState<'All' | 'Pending' | 'Approved' | 'Rejected'>(defaultFilter);
  
  // Update filter when defaultFilter prop changes
  useEffect(() => {
    setStatusFilter(defaultFilter);
  }, [defaultFilter]);
  
  // Modal state for edit and delete
  const [editMemberModalOpen, setEditMemberModalOpen] = useState(false);
  const [editMember, setEditMember] = useState<Member | null>(null);
  const [deleteMemberModalOpen, setDeleteMemberModalOpen] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<Member | null>(null);

  const handleEditMember = (member: Member) => {
    setEditMember(member);
    setEditMemberModalOpen(true);
  };

  const handleSaveMember = async (updatedMember: Member) => {
    try {
      // Call the API to update the member
      const response = await fetch(`${API_BASE_URL}/potentialnewmembers/${updatedMember.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(updatedMember),
      });

      if (!response.ok) {
        throw new Error('Failed to update member');
      }

      // Refresh the parent's data
      if (onEdit) {
        onEdit(0);
      }

      setEditMemberModalOpen(false);
      setEditMember(null);
    } catch (error) {
      console.error('Error updating member:', error);
      // Handle error appropriately
    }
  };

  const handleDeleteMember = (member: Member) => {
    setMemberToDelete(member);
    setDeleteMemberModalOpen(true);
  };

  const handleConfirmDeleteMember = async (memberId: number) => {
    try {
      // Call the API to delete the member
      const response = await fetch(`${API_BASE_URL}/potentialnewmembers/${memberId}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete member');
      }

      // Refresh the parent's data
      if (onDelete) {
        onDelete(0);
      }

      setDeleteMemberModalOpen(false);
      setMemberToDelete(null);
    } catch (error) {
      console.error('Error deleting member:', error);
      // Handle error appropriately
    }
  };
  
  const filtered = members.filter((m) =>
    (m.name?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (m.email?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (m.phoneNumber?.toLowerCase() || '').includes(searchQuery.toLowerCase()) ||
    (m.business_Or_Employer?.toLowerCase() || '').includes(searchQuery.toLowerCase())
  );

  return (
    <section className="dashboard-section" style={{ marginTop: '20px', maxHeight: 'none' }}>
      <div className="dashboard-section-header">
        <h2 style={{ fontWeight: 700 }}>All Members</h2>
      </div>
      {/* Status Filter and Search Section */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
        {/* Status filter on the left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Always show All button */}
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              background: statusFilter === 'All' ? '#e9eaf0' : '#f6f7fb',
              color: statusFilter === 'All' ? '#222' : '#888',
              borderRadius: 20,
              padding: '6px 16px',
              fontSize: '0.95rem',
              fontWeight: statusFilter === 'All' ? 700 : 500,
              border: statusFilter === 'All' ? '2px solid #666' : '1px solid #e0e6f0',
              cursor: statusFilter === 'All' ? 'default' : 'pointer',
              opacity: statusFilter === 'All' ? 1 : 0.85,
              boxShadow: statusFilter === 'All' ? '0 0 0 2px #dbeafe' : undefined,
              marginRight: 8,
              transition: 'box-shadow 0.15s, opacity 0.15s, border-color 0.15s',
              outline: statusFilter === 'All' ? 'none' : undefined
            }}
            onClick={() => statusFilter !== 'All' && setStatusFilter('All')}
          >
            All
            {statusFilter === 'All' && (
              <span style={{ marginLeft: 6, fontWeight: 700, fontSize: '1rem', color: '#43a047', display: 'flex', alignItems: 'center' }}>
                ✓
              </span>
            )}
          </span>
          
          {/* Show other status buttons */}
          {['Pending', 'Approved', 'Rejected'].map(status => {
            const isActive = statusFilter === status;
            let background, color, border;
            if (status === 'Pending') {
              background = '#ffe8b3'; color = '#e6a23c'; border = isActive ? '2px solid #d4a017' : '1px solid #ffe0b2';
            } else if (status === 'Approved') {
              background = '#d1e7ff'; color = '#3366ff'; border = isActive ? '2px solid #2b5bff' : '1px solid #dbeafe';
            } else if (status === 'Rejected') {
              background = '#ffd1d1'; color = '#d32f2f'; border = isActive ? '2px solid #b71c1c' : '1px solid #ffcdd2';
            }
            return (
              <span
                key={status}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  background,
                  color,
                  borderRadius: 20,
                  padding: '6px 16px',
                  fontSize: '0.95rem',
                  fontWeight: isActive ? 700 : 500,
                  border,
                  cursor: isActive ? 'default' : 'pointer',
                  opacity: isActive ? 1 : 0.85,
                  boxShadow: isActive ? '0 0 0 2px #dbeafe' : undefined,
                  marginRight: 8,
                  transition: 'box-shadow 0.15s, opacity 0.15s, border-color 0.15s',
                  outline: isActive ? 'none' : undefined
                }}
                onClick={() => !isActive && setStatusFilter(status as 'Pending' | 'Approved' | 'Rejected')}
              >
                {status}
                {isActive && (
                  <span
                    style={{
                      marginLeft: 8,
                      cursor: 'pointer',
                      fontWeight: 700,
                      fontSize: '1.2rem',
                      color,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 2px',
                      lineHeight: 1,
                      width: '16px',
                      height: '16px'
                    }}
                    title="Clear status filter"
                    onClick={(e) => {
                      e.stopPropagation();
                      setStatusFilter('All');
                    }}
                  >
                    ×
                  </span>
                )}
              </span>
            );
          })}
        </div>
        {/* Search on the right */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            className="dashboard-search"
            placeholder="Search members..."
            value={searchQuery}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>
      </div>
      <div className="dashboard-table-wrapper members-table-scroll" style={{ maxHeight: 'none' }}>
        <table className="dashboard-table">
          <thead>
            <tr>
              <th style={{ fontSize: '0.95rem' }}>Name</th>
              <th style={{ fontSize: '0.95rem' }}>Email</th>
              <th style={{ fontSize: '0.95rem' }}>Phone</th>
              <th style={{ fontSize: '0.95rem' }}>Business</th>
              <th style={{ fontSize: '0.95rem' }}>Applied On</th>
              <th style={{ fontSize: '0.95rem' }}>Status</th>
              <th colSpan={2} style={{ fontSize: '0.95rem' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered
              .filter(m => statusFilter === 'All' || (m.status || '') === statusFilter)
              .length === 0 ? (
              <tr><td colSpan={7} style={{ textAlign: 'center', color: '#888', fontSize: '0.9rem', padding: '40px 20px' }}>
                {statusFilter !== 'All' || searchQuery ? 
                  'No members match your current filters. Try adjusting your search or status filter.' : 
                  'No members found. No potential new members have applied yet.'
                }
              </td></tr>
            ) : (
              filtered
                .filter(m => statusFilter === 'All' || (m.status || '') === statusFilter)
                .map((m) => (
                <tr key={m.id}>
                  <td style={{ fontSize: '0.9rem' }}>{m.name || 'No name'}</td>
                  <td style={{ fontSize: '0.9rem' }}>{m.email || 'No email'}</td>
                  <td style={{ fontSize: '0.9rem' }}>{m.phoneNumber || 'No phone'}</td>
                  <td style={{ fontSize: '0.9rem' }}>{m.business_Or_Employer || 'No business'}</td>
                  <td style={{ fontSize: '0.9rem' }}>{m.applied_On ? new Date(m.applied_On).toLocaleDateString() : 'No date'}</td>
                  <td>
                    <span 
                      className={`dashboard-badge dashboard-badge-${(m.status?.toLowerCase() || 'unknown')}`} 
                      style={{ fontSize: '0.9rem', cursor: 'pointer' }}
                      title={`Filter by ${m.status || 'Unknown'}`}
                      onClick={() => setStatusFilter((m.status || 'Unknown') as 'Pending' | 'Approved' | 'Rejected')}
                    >
                      {m.status || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <button className="dashboard-icon-btn edit" title="Edit" aria-label="Edit" onClick={() => handleEditMember(m)}>
                      <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="10" fill="#eaf3ff" />
                        <path d="M13 7l-6 6v2h2l6-6-2-2z" stroke="#4f8cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="none" />
                        <path d="M11 5l2 2" stroke="#4f8cff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                    <button className="dashboard-icon-btn delete" title="Delete" aria-label="Delete" onClick={() => handleDeleteMember(m)}>
                      <svg width="20" height="20" fill="none" viewBox="0 0 20 20">
                        <circle cx="10" cy="10" r="10" fill="#fff0f0" />
                        <path d="M6.5 6.5l7 7m0-7l-7 7" stroke="#f36b6b" strokeWidth="2" strokeLinecap="round" />
                      </svg>
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {editMemberModalOpen && editMember && (
        <EditMemberModal
          member={editMember}
          isOpen={editMemberModalOpen}
          onClose={() => setEditMemberModalOpen(false)}
          onSave={handleSaveMember}
        />
      )}
      {deleteMemberModalOpen && memberToDelete && (
        <DeleteMemberModal
          member={memberToDelete}
          isOpen={deleteMemberModalOpen}
          onClose={() => setDeleteMemberModalOpen(false)}
          onConfirm={() => handleConfirmDeleteMember(memberToDelete.id)}
        />
      )}
    </section>
  );
};

export default MembersPage;
